import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthenticationContext from '../auth/AuthenticationContext';
import { AuthenticationState } from '../auth/AuthenticationContext';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

type ThemeContextValue = {
  themeMode: ThemeMode;
  themeName: ResolvedTheme;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
};

const THEME_STORAGE_KEY = 'leedflow-theme-mode';

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const getStoredTheme = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  }
  return 'system';
};

const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  return 'light'; // Dark theme disabled
  // if (mode === 'system') {
  //   return getSystemTheme();
  // }
  // return mode;
};

export const ThemeProvider: React.FC<React.PropsWithChildren<{ defaultTheme?: ThemeMode }>> = ({
  defaultTheme,
  children,
}) => {
  const authContext = useContext(AuthenticationContext);
  const isAuthenticated = authContext?.authenticationState === AuthenticationState.AUTHENTICATED;

  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    return getStoredTheme() || defaultTheme || 'system';
  });
  const [themeName, setThemeName] = useState<ResolvedTheme>(() =>
    isAuthenticated ? resolveTheme(themeMode) : 'light'
  );

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, []);

  const toggleTheme = useCallback(() => {
    const modes: ThemeMode[] = ['light', 'dark', 'system'];
    const currentIndex = modes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setThemeMode(modes[nextIndex]);
  }, [themeMode, setThemeMode]);

  // Force light theme when not authenticated
  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const themeKey = `${isAuthenticated}:${themeMode}`;
  const [lastThemeKey, setLastThemeKey] = useState(themeKey);
  if (themeKey !== lastThemeKey) {
    setLastThemeKey(themeKey);
    setThemeName(isAuthenticated ? resolveTheme(themeMode) : 'light');
  }

  // Listen for system theme changes
  useEffect(() => {
    if (!isAuthenticated || themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setThemeName(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isAuthenticated, themeMode]);

  return (
    <ThemeContext.Provider value={{ themeMode, themeName, setThemeMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
